"use client"

/* eslint-disable @next/next/no-img-element */
import Link from 'next/link'
import { Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { estimateReadingMinutes, formatBlogDate, type BlogArticle } from '@/lib/blog'

function getInitials(title: string) {
  return title.replace(/[^가-힣a-zA-Z0-9]/g, '').slice(0, 2) || '글'
}

export function LatestBlogSwiper({ articles }: { articles: BlogArticle[] }) {
  if (!articles.length) {
    return <div className="rounded-3xl border border-[#3A3A3A] bg-[#1A1A1A] p-8 text-center">
      <h3 className="text-2xl font-black text-[#F9FAFB]">등록된 최신 블로그 글이 없습니다</h3>
      <p className="mt-3 text-[#D1D5DB]">관리자에서 공개 블로그 글을 등록하면 최신 10개가 이미지 카드 스와이퍼로 표시됩니다.</p>
    </div>
  }

  return <Swiper
    modules={[Navigation, Pagination]}
    navigation
    pagination={{ clickable: true }}
    spaceBetween={18}
    slidesPerView={1.08}
    breakpoints={{
      640: { slidesPerView: 1.6, spaceBetween: 18 },
      768: { slidesPerView: 2.15, spaceBetween: 20 },
      1024: { slidesPerView: 3.15, spaceBetween: 22 },
      1280: { slidesPerView: 4.15, spaceBetween: 24 },
    }}
    className="latest-blog-swiper !overflow-visible px-0 pb-16"
  >
    {articles.map((article) => <SwiperSlide key={article.id} className="!h-auto">
      <Link href={`/blog/${article.slug}`} className="card-hover group flex h-full min-w-0 flex-col overflow-hidden rounded-3xl border border-[#3A3A3A] bg-[#1A1A1A] shadow-xl shadow-[#111827]/8">
        <div className="relative aspect-[4/3] min-w-0 overflow-hidden bg-[#2A2A2A]">
          {article.cover_image_url ? <img src={article.cover_image_url} alt={`${article.title} 대표이미지`} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" /> : <div className="grid h-full place-items-center bg-[radial-gradient(circle_at_top_left,#FF4500_0,#FFB199_28%,#2A2A2A_72%)] p-6 text-center">
            <span className="rounded-full bg-white/90 px-5 py-4 text-3xl font-black text-[#FF4500] shadow-lg">{getInitials(article.title)}</span>
          </div>}
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#111827]/90 to-transparent p-4">
            <span className="rounded-full bg-[#FF4500] px-3 py-1 text-xs font-black text-white">{article.category}</span>
          </div>
        </div>
        <div className="flex flex-1 flex-col p-5">
          <div className="flex flex-wrap items-center gap-2 text-xs font-black text-[#FFB199]"><span>{formatBlogDate(article.published_at)}</span><span>·</span><span>{estimateReadingMinutes(article.content)}분 읽기</span></div>
          <h3 className="latest-blog-card-title mt-3 line-clamp-2 min-h-[3.1rem] break-words text-xl font-black leading-tight tracking-[-0.05em] text-[#F9FAFB]">{article.title}</h3>
          <p className="mt-3 line-clamp-3 break-words text-sm font-bold leading-6 text-[#D1D5DB]">{article.summary}</p>
          <div className="mt-auto flex flex-wrap gap-2 pt-5">
            <span className="rounded-full bg-[#2A2A2A] px-3 py-1 text-xs font-black text-[#F3F4F6]">읽음 {(article.view_count ?? 0).toLocaleString('ko-KR')}</span>
            <span className="rounded-full border border-[#FF4500]/60 px-3 py-1 text-xs font-black text-[#FFB199]">자세히 보기 →</span>
          </div>
        </div>
      </Link>
    </SwiperSlide>)}
  </Swiper>
}
